import { RootStateInterface } from '@/store/rootState';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { BrokerStateInterface } from '../interfaces/brokerState';
import { File } from '@/modules/clients/models/File';
import { BrokerApi } from '../api/broker';
import { BrokerApiRecord } from '../interfaces/brokerApiRecord';
import { DataSourceRequest } from '../models/DataSourceRequest';
import { DataSourceResponse } from '../models/DataSourceResponse';
import { BrokersListApi } from '../api/brokersList';

export const state: BrokerStateInterface = {
  brokerId: null,
  brokerCode: null,
  company: {
    id: null,
    owner: null,
    name: null,
    logo: null,
    featureFlags: null,
    companyOwners: null,
  },
  selfBroker: null,
  actingBroker: null,
  brokerListCache: {},
};

function sliceFromBroker(b: BrokerApiRecord | null): Partial<BrokerStateInterface> | null {
  if (!b?.id || !b.company) {
    return null;
  }
  return {
    brokerId: b.id,
    brokerCode: b.brokerCode || null,
    company: b.company,
  };
}

export const mutations: MutationTree<BrokerStateInterface> = {
  updateBrokerId(oldState: BrokerStateInterface, payload: number | null) {
    oldState.brokerId = payload;
  },

  updateBrokerCode(oldState: BrokerStateInterface, payload: string | null) {
    oldState.brokerCode = payload;
  },

  setSelfBroker(st: BrokerStateInterface, payload: BrokerApiRecord | null) {
    st.selfBroker = payload;
  },

  setActingBroker(st: BrokerStateInterface, payload: BrokerApiRecord | null) {
    st.actingBroker = payload;
  },

  update(oldState: BrokerStateInterface, payload: BrokerStateInterface | null) {
    if (payload === null) {
      oldState.company.id = null;
      oldState.company.owner = null;
      oldState.company.name = null;
      oldState.company.logo = null;
      oldState.company.featureFlags = null;
      oldState.company.companyOwners = null;
      oldState.brokerId = null;
      oldState.brokerCode = null;
      oldState.brokerListCache = {};
      return;
    }
    oldState.company.id = payload?.company?.id ?? null;
    oldState.company.owner = payload?.company?.owner ?? null;
    oldState.company.name = payload?.company?.name ?? null;
    oldState.company.logo = payload?.company?.logo ?? null;
    oldState.company.featureFlags = payload.company.featureFlags ?? null;
    oldState.company.companyOwners = payload.company.companyOwners ?? null;
    oldState.brokerId = payload?.brokerId ?? null;
    oldState.brokerCode =
      payload?.brokerCode !== undefined ? payload.brokerCode : oldState.brokerCode;
  },

  applyFromBrokerPayload(st: BrokerStateInterface, broker: BrokerApiRecord | null) {
    const slice = sliceFromBroker(broker);
    if (!slice?.brokerId || !broker?.company) {
      return;
    }
    st.company.id = broker.company.id ?? null;
    st.company.owner = broker.company.owner ?? null;
    st.company.name = broker.company.name ?? null;
    st.company.logo = broker.company.logo ?? null;
    st.company.featureFlags = broker.company.featureFlags ?? null;
    st.company.companyOwners = broker.company.companyOwners ?? null;
    st.brokerId = slice.brokerId!;
    st.brokerCode = slice.brokerCode ?? null;
  },

  clearBrokerSnapshots(st: BrokerStateInterface) {
    st.selfBroker = null;
    st.actingBroker = null;
  },

  setBrokerListCache(
    st: BrokerStateInterface,
    payload: { requestKey: string; data: DataSourceResponse },
  ) {
    st.brokerListCache = {
      ...st.brokerListCache,
      [payload.requestKey]: payload.data,
    };
  },
  invalidateBrokerListCache(st: BrokerStateInterface, requestKey?: string) {
    if (!requestKey) {
      st.brokerListCache = {};
      return;
    }
    const next = { ...st.brokerListCache };
    delete next[requestKey];
    st.brokerListCache = next;
  },

  updateLogo(oldState: BrokerStateInterface, payload: File | null) {
    oldState.company.logo = payload;
  },
};

export const getters: GetterTree<BrokerStateInterface, RootStateInterface> = {
  effectiveBroker(st, _g, rootState): BrokerApiRecord | null {
    const self = st.selfBroker;
    const actingId = rootState.personalAssistants?.currentActingBrokerId ?? null;
    if (!self?.id) {
      // PA/admin users have no selfBroker — return actingBroker if loaded
      const acting = st.actingBroker;
      return (acting && acting.id === actingId) ? acting : null;
    }
    if (!actingId || actingId === self.id) {
      return self;
    }
    const acting = st.actingBroker;
    if (acting && acting.id === actingId) {
      return acting;
    }
    return self;
  },
};

let bootstrapInFlight: Promise<BrokerApiRecord | null> | null = null;
const brokerListInFlight: Record<string, Promise<DataSourceResponse> | undefined> = {};

export const actions: ActionTree<BrokerStateInterface, RootStateInterface> = {
  async bootstrapBrokerContext({ commit, rootState }): Promise<BrokerApiRecord | null> {
    const run = async (): Promise<BrokerApiRecord | null> => {
      const api = new BrokerApi();
      const self = await api.getBroker();
      if (!self?.id || !self.company) {
        commit('clearBrokerSnapshots');
        commit('update', null);
        const actingId = rootState.personalAssistants?.currentActingBrokerId ?? null;
        if (actingId) {
          const acting = await api.getBroker(actingId);
          if (acting?.id) {
            commit('setActingBroker', acting);
            commit('applyFromBrokerPayload', acting);
            return acting;
          }
        }
        return null;
      }
      commit('setSelfBroker', self);
      commit('applyFromBrokerPayload', self);
      const companyOwners = self.company.companyOwners || [];
      const isDefaultActingSelf =
        self.id === self.company.owner ||
        companyOwners.some((o: { broker?: { id: number } }) => o.broker?.id === self.id);
      if (isDefaultActingSelf) {
        commit('personalAssistants/updateCurrentActingBrokerId', self.id, { root: true });
        commit('personalAssistants/updateCurrentActingBrokerUser', self.user, { root: true });
      }
      commit('auth/setBroker', self.id, { root: true });

      const actingId = rootState.personalAssistants?.currentActingBrokerId ?? null;
      if (actingId && actingId !== self.id) {
        const acting = await api.getBroker(actingId);
        if (acting?.id) {
          commit('setActingBroker', acting);
          commit('applyFromBrokerPayload', acting);
        } else {
          commit('setActingBroker', self);
          commit('applyFromBrokerPayload', self);
        }
      } else {
        commit('setActingBroker', self);
      }
      return self;
    };

    if (bootstrapInFlight) {
      return bootstrapInFlight;
    }
    bootstrapInFlight = run().finally(() => {
      bootstrapInFlight = null;
    });
    return bootstrapInFlight;
  },

  async refreshBrokerContext({ commit, rootState }): Promise<void> {
    const api = new BrokerApi();
    const self = await api.getBroker();
    if (!self?.id) {
      return;
    }
    commit('setSelfBroker', self);
    const actingId = rootState.personalAssistants?.currentActingBrokerId ?? null;
    if (actingId && actingId !== self.id) {
      const acting = await api.getBroker(actingId);
      if (acting?.id) {
        commit('setActingBroker', acting);
        commit('applyFromBrokerPayload', acting);
      } else {
        commit('setActingBroker', self);
        commit('applyFromBrokerPayload', self);
      }
    } else {
      commit('setActingBroker', self);
      commit('applyFromBrokerPayload', self);
    }
  },

  async loadActingBrokerAfterSwitch(
    { commit },
    actingId: number,
  ): Promise<BrokerApiRecord | null> {
    const api = new BrokerApi();
    const broker = await api.getBroker(actingId);
    if (!broker?.id) {
      return null;
    }
    commit('setActingBroker', broker);
    commit('applyFromBrokerPayload', broker);
    commit('personalAssistants/updateCurrentActingBrokerUser', broker.user, { root: true });
    return broker;
  },
  async fetchBrokerListCached(
    { state: st, commit },
    payload: {
      request: DataSourceRequest;
      actingBrokerId?: number | null;
      forceRefresh?: boolean;
    },
  ): Promise<DataSourceResponse> {
    const requestKey = JSON.stringify({
      request: payload.request,
      actingBrokerId: payload.actingBrokerId ?? null,
    });
    if (!payload.forceRefresh && st.brokerListCache[requestKey]) {
      return st.brokerListCache[requestKey];
    }
    const existingInFlight = brokerListInFlight[requestKey];
    if (existingInFlight) {
      return existingInFlight;
    }

    const run = async () => {
      const api = new BrokersListApi();
      const data = await api.list(payload.request, payload.actingBrokerId);
      commit('setBrokerListCache', { requestKey, data });
      return data;
    };
    const inFlight = run().finally(() => {
      delete brokerListInFlight[requestKey];
    });
    brokerListInFlight[requestKey] = inFlight;
    return inFlight;
  },
};

const namespaced: boolean = true;

export const brokers: Module<BrokerStateInterface, RootStateInterface> = {
  namespaced,
  state,
  mutations,
  getters,
  actions,
};
